import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { isUnlimited } from "src/ui/utils/limit";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { useGetAllWorkspacesQuery, useGetWorkspaceFeaturesQuery } from "@dashboard/workspaces/api";
import { FeatureType, Workspace } from "@dashboard/workspaces/types";
import { Box, TableRow, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { DateCell } from "@shared/table/DateCell";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableCell, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";
import {
    createColumnHelper,
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

const useStyles = makeStyles()((theme: Theme) => ({
    expiredSubscription: {
        backgroundColor: theme.palette.emteria.warning,
    },
}));

const columnHelper = createColumnHelper<API.Workspace>();

const LimitCell = ({ workspaceId }: { workspaceId: number }) => {
    const { t } = useTranslation("pages");
    const { data: workspaceFeatures = [] } = useGetWorkspaceFeaturesQuery({ id: workspaceId, includeUsage: false });
    const limit = workspaceFeatures.find((feature) => feature.feature === "LicenseLimit");
    return (
        <MinimalTableCell align="left">
            {isUnlimited(limit?.max, "LicenseLimit") ? t("adminExpirationsPage.unlimited") : limit?.max}
        </MinimalTableCell>
    );
};

const columns = [
    columnHelper.accessor("id", {
        id: "id",
        header: i18next.t("pages:adminExpirationsPage.column.id"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
    columnHelper.accessor("name", {
        id: "name",
        header: i18next.t("pages:adminExpirationsPage.column.name"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
    columnHelper.accessor("members", {
        id: "owner",
        header: i18next.t("common:label.owner"),
        cell: (info) => {
            const ownerEmail = info.row.original.members.find((member) => member.role === "Owner")?.email ?? "-";
            return <MinimalTableCell>{ownerEmail}</MinimalTableCell>;
        },
        enableSorting: false,
    }),
    columnHelper.accessor("organizationName", {
        id: "organizationName",
        header: i18next.t("pages:adminExpirationsPage.column.organization"),
        cell: (info) => <MinimalTableCell>{info.getValue() || "-"}</MinimalTableCell>,
    }),
    columnHelper.accessor("subscription.chargeId", {
        id: "charge",
        header: i18next.t("pages:adminExpirationsPage.column.charge"),
        cell: (info) => <MinimalTableCell>{info.getValue() || "-"}</MinimalTableCell>,
    }),
    columnHelper.display({
        id: "limit",
        header: i18next.t("pages:adminExpirationsPage.column.limit"),
        cell: (info) => <LimitCell workspaceId={info.row.original.id} />,
    }),
    columnHelper.accessor("subscription.creationDate", {
        id: "created",
        header: i18next.t("pages:adminExpirationsPage.column.creationDate"),
        cell: (info) => <DateCell value={info.getValue()} />,
    }),
    columnHelper.accessor("subscription.expirationDate", {
        id: "expired",
        header: i18next.t("pages:adminExpirationsPage.column.expirationDate"),
        cell: (info) => <DateCell value={info.getValue()} />,
    }),
];

const rowsPerPageOptions = [10, 20, 50];

const fromExpirationDate = new Date();
fromExpirationDate.setMonth(fromExpirationDate.getMonth() - 24);

const toExpirationDate = new Date();
toExpirationDate.setMonth(toExpirationDate.getMonth() + 3);

const AdminExpirationsPage = () => {
    const { classes } = useStyles();
    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const [sorting, setSorting] = React.useState<{ id: string; desc: boolean } | null>({
        id: "expired",
        desc: false,
    });
    const [pageIndex, setPageIndex] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(rowsPerPageOptions[0]);

    const { t } = useTranslation("pages");

    const workspacesQuery = useGetAllWorkspacesQuery({
        pageIndex: pageIndex + pageIndexOffset,
        pageSize,
        includeTerminated: false,
        billingType: "manual",
        sortCriteria: sorting?.id,
        sortDirection: sorting?.desc ? "descending" : "ascending",
        fromExpirationDate: fromExpirationDate.toISOString(),
        toExpirationDate: toExpirationDate.toISOString(),
        featureType: FeatureType.LicenseLimit,
        minFeatureValue: 5,
    });
    const { data, isLoading } = workspacesQuery;
    useQueryErrorToast(workspacesQuery);
    const workspaces = data?.list ?? [];
    const count = data?.count ?? 0;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, pageSize, workspaces.length]);

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting ? [sorting] : []);
        if (newValue.length > 0) {
            setSorting(newValue[0]);
        } else {
            setSorting(null);
        }
        setPageIndex(0);
    };

    const table = useReactTable({
        data: workspaces,
        columns,
        state: { sorting: sorting ? [sorting] : [] },
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
    });

    return (
        <PageContainer fitViewport>
            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody<Workspace>
                                noDataMessage={t("adminExpirationsPage.emptyMessage")}
                                loading={isLoading}
                                table={table}
                            >
                                {table.getRowModel().rows.map((row) => {
                                    const expirationDate = row.original.subscription.expirationDate;
                                    const isExpired = expirationDate ? new Date(expirationDate).getTime() <= Date.now() : false;
                                    return (
                                        <HoverRow
                                            key={row.id}
                                            clickable={false}
                                            className={isExpired ? classes.expiredSubscription : ""}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <React.Fragment key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </React.Fragment>
                                            ))}
                                        </HoverRow>
                                    );
                                })}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>
                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    count={count}
                    rowsPerPageOptions={rowsPerPageOptions}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_e, page) => setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : pageSize;
                        setPageSize(size);
                        setPageIndex(0);
                    }}
                />
            </Box>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminExpirationsPage, {
        title: i18next.t("pages:adminExpirationsPage.metaTitle"),
        description: i18next.t("pages:adminExpirationsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaSalesRole, EmteriaRoles.EmteriaAccountantRole],
);
